import {
	ArrowLeft,
	ArrowRight,
	Check,
	ChevronDown,
	Download,
	Plus,
	Search,
	X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import packageModalApiService from "../services/packageModalApiService";

const OA_ORGANIZATION_NAMES = [
	"OpenAudience",
	"OpenAudience Demo",
	"openx demo",
	"openx",
	"OpenAudience Australia",
	"OpenM Org.",
];

const OA_PROVIDER_NAMES = ["openaudience", "openaudiencecustom", "openm"];

// Helper functions for audience categorization
const isOAOrganization = (audience) => {
	if (!audience || !audience.accountName) {
		return false;
	}
	const accountName = audience.accountName.trim();
	return OA_ORGANIZATION_NAMES.some((name) =>
		accountName.toLowerCase().includes(name.toLowerCase()),
	);
};

const isOAProvider = (audience) => {
	if (
		!audience ||
		!audience.audienceVector ||
		!audience.audienceVector.directAudienceProvider
	) {
		return false;
	}
	const provider = audience.audienceVector.directAudienceProvider
		.trim()
		.toLowerCase();
	return OA_PROVIDER_NAMES.includes(provider);
};

const categorizeAudience = (audience) => {
	const isOrgOA = isOAOrganization(audience);
	const isProvOA = isOAProvider(audience);

	if (!isProvOA && !isOrgOA) {
		return "non_oa_non_oa";
	} else if (isProvOA && !isOrgOA) {
		return "oa_provider_non_oa_org";
	} else if (isProvOA && isOrgOA) {
		return "oa_provider_oa_org";
	} else {
		return "non_oa_provider_oa_org";
	}
};

// Format large numbers for display
const formatNumber = (num) => {
	if (num >= 1000000) {
		return (num / 1000000).toFixed(1) + "M";
	} else if (num >= 1000) {
		return (num / 1000).toFixed(1) + "K";
	}
	return num?.toString() || "0";
};

// Format percentage values
const formatPercentage = (value) => {
	if (value && typeof value === "number") {
		return (value * 100).toFixed(1) + "%";
	}
	return "0%";
};

const AudienceCard = ({
	audience,
	isSelected,
	onToggle,
	selectedProvider,
	selectedOrg,
}) => {
	// Extract data from nested audienceVector structure
	const audienceData = audience.audienceVector || audience;
	const metrics = audience.audienceMetrics || {};

	console.log("🔍 [DEBUG] AudienceCard - Raw audience object:", audience);
	console.log(
		"🔍 [DEBUG] AudienceCard - Extracted audienceData:",
		audienceData,
	);
	console.log("🔍 [DEBUG] AudienceCard - Selected filters:", {
		selectedProvider,
		selectedOrg,
	});

	const category = categorizeAudience({
		accountName: audienceData.accountName,
		audienceVector: {
			directAudienceProvider: audienceData.directAudienceProvider,
		},
	});

	const isOrgOA = isOAOrganization({ accountName: audienceData.accountName });
	const isProvOA = isOAProvider({
		audienceVector: {
			directAudienceProvider: audienceData.directAudienceProvider,
		},
	});

	console.log("🔍 [DEBUG] AudienceCard - Categorization:", {
		category,
		isOrgOA,
		isProvOA,
		selectedProvider,
		selectedOrg,
	});

	// Check if audience matches selected filters
	const matchesProvider =
		!selectedProvider ||
		(selectedProvider === "OA" && isProvOA) ||
		(selectedProvider === "OAA" && !isProvOA);

	const matchesOrg =
		!selectedOrg ||
		(selectedOrg === "OA" && isOrgOA) ||
		(selectedOrg === "OAA" && !isOrgOA);

	console.log("🔍 [DEBUG] AudienceCard - Filter matching:", {
		matchesProvider,
		matchesOrg,
		willRender: matchesProvider && matchesOrg,
	});

	if (!matchesProvider || !matchesOrg) {
		console.log("🔍 [DEBUG] AudienceCard - Filtered out, not rendering");
		return null;
	}

	console.log(
		"🔍 [DEBUG] AudienceCard - Rendering card for audience:",
		audienceData.audienceId,
	);

	return (
		<div
			className={`result-card audience-card ${isSelected ? "selected" : ""}`}
			onClick={() => onToggle(`audience-${audienceData.audienceId}`, audience)}
		>
			<div className="card-header">
				<h4 className="card-title">
					{audienceData.audienceName || "Unnamed Audience"}
				</h4>
				<div className="card-score">
					<span className="score-label">Score</span>
					<span className="score-value">
						{audience.compositeScore?.toFixed(1) || "0.0"}
					</span>
				</div>
				{isSelected && <Check size={20} className="selected-icon" />}
			</div>

			<div className="card-metrics">
				<div className="metric">
					<span className="metric-label">Reach</span>
					<span className="metric-value">
						{formatNumber(metrics.cookies || metrics.reach)}
					</span>
				</div>
				<div className="metric">
					<span className="metric-label">Impressions</span>
					<span className="metric-value">
						{formatNumber(metrics.soldImpressions || metrics.impressions)}
					</span>
				</div>
				<div className="metric">
					<span className="metric-label">CTR</span>
					<span className="metric-value">{formatPercentage(metrics.ctr)}</span>
				</div>
				<div className="metric">
					<span className="metric-label">Viewability</span>
					<span className="metric-value">
						{formatPercentage(metrics.viewability)}
					</span>
				</div>
			</div>

			<div className="card-footer">
				<span className="audience-type">
					{audienceData.audienceType || "Standard"}
				</span>
				<span className="account-name">
					{audienceData.accountName || "Unknown"}
				</span>
			</div>
		</div>
	);
};

// Domain Card Component
const DomainCard = ({ domain, isSelected, onToggle }) => {
	return (
		<div
			className={`result-card domain-card ${isSelected ? "selected" : ""}`}
			onClick={() => onToggle(`domain-${domain.id}`, domain)}
		>
			<div className="card-header">
				<h4 className="card-title">
					{domain.domainName || domain.name || "Unknown Domain"}
				</h4>
				<div className="card-score">
					<span className="score-label">Score</span>
					<span className="score-value">
						{domain.score?.toFixed(1) || "0.0"}
					</span>
				</div>
				{isSelected && <Check size={20} className="selected-icon" />}
			</div>

			<div className="card-metrics">
				<div className="metric">
					<span className="metric-label">Reach</span>
					<span className="metric-value">{formatNumber(domain.reach)}</span>
				</div>
				<div className="metric">
					<span className="metric-label">Impressions</span>
					<span className="metric-value">
						{formatNumber(domain.impressions)}
					</span>
				</div>
				<div className="metric">
					<span className="metric-label">CTR</span>
					<span className="metric-value">{formatPercentage(domain.ctr)}</span>
				</div>
				<div className="metric">
					<span className="metric-label">Viewability</span>
					<span className="metric-value">
						{formatPercentage(domain.viewability)}
					</span>
				</div>
			</div>

			<div className="card-footer">
				<span className="domain-category">{domain.category || "General"}</span>
			</div>
		</div>
	);
};

// Results Display Component - Shows cards view
const ResultsDisplay = ({
	audienceResults,
	domainResults,
	isLoading,
	selectedProvider,
	selectedOrg,
	selectedResults,
	onResultToggle,
}) => {
	if (isLoading) {
		return (
			<div className="loading-state">
				<div className="loading-spinner"></div>
				<span>Fetching results...</span>
			</div>
		);
	}

	let audiences = [];
	console.log("🔍 [DEBUG] Processing audience results:", audienceResults);

	if (audienceResults && typeof audienceResults === "object") {
		// Handle the nested structure like {sports: {data: [...]}, news: {data: [...]}}
		console.log(
			"🔍 [DEBUG] Audience results keys:",
			Object.keys(audienceResults),
		);

		// Check if it's the nested category structure
		const categoryKeys = Object.keys(audienceResults).filter(
			(key) =>
				audienceResults[key] &&
				typeof audienceResults[key] === "object" &&
				audienceResults[key].data &&
				Array.isArray(audienceResults[key].data),
		);

		if (categoryKeys.length > 0) {
			// Flatten all audiences from all categories
			categoryKeys.forEach((categoryKey) => {
				const categoryData = audienceResults[categoryKey];
				console.log(
					`🔍 [DEBUG] Processing category: ${categoryKey}, audiences: ${categoryData.data.length}`,
				);

				// Add category info to each audience for later filtering
				const categoryAudiences = categoryData.data.map((audience) => ({
					...audience,
					categoryKey: categoryKey, // Add category identifier
				}));

				audiences = audiences.concat(categoryAudiences);
			});

			console.log(
				"🔍 [DEBUG] Total audiences after flattening:",
				audiences.length,
			);
		} else {
			// Fallback to existing logic for other structures
			if (Array.isArray(audienceResults)) {
				audiences = audienceResults;
			} else if (
				audienceResults.results &&
				Array.isArray(audienceResults.results)
			) {
				audiences = audienceResults.results;
			} else if (audienceResults.data && Array.isArray(audienceResults.data)) {
				audiences = audienceResults.data;
			}
		}
	}

	console.log("🔍 [DEBUG] Final audiences array length:", audiences.length);
	console.log("🔍 [DEBUG] First few audiences:", audiences.slice(0, 3));

	// Process domain results - only show domains from cumulativeData
	let domains = [];
	const cumulativeDataDomainIds = new Set();

	console.log("🔍 [DEBUG] Raw domainResults:", domainResults);

	if (domainResults && typeof domainResults === "object") {
		// First pass: Extract domain IDs from cumulativeData
		let cumulativeData = null;

		// Handle different possible structures for cumulativeData
		if (domainResults.body && domainResults.body.cumulativeData) {
			cumulativeData = domainResults.body.cumulativeData;
		} else if (domainResults.cumulativeData) {
			cumulativeData = domainResults.cumulativeData;
		}

		if (cumulativeData && Array.isArray(cumulativeData)) {
			console.log("🔍 [DEBUG] Found cumulativeData array:", cumulativeData);

			cumulativeData.forEach((segment) => {
				console.log("🔍 [DEBUG] Processing segment:", segment);
				if (segment.domainIds) {
					try {
						// Parse the JSON string containing domain IDs
						const ids = JSON.parse(segment.domainIds);
						console.log(
							"🔍 [DEBUG] Parsed domain IDs for segment:",
							segment.segment,
							ids,
						);

						if (Array.isArray(ids)) {
							ids.forEach((id) => {
								cumulativeDataDomainIds.add(String(id));
								console.log(
									"🔍 [DEBUG] Added domain ID to allowed set:",
									String(id),
								);
							});
						}
					} catch (e) {
						console.warn(
							"Could not parse domainIds from segment:",
							segment.segment,
							segment.domainIds,
							e,
						);
					}
				}
			});
		}

		console.log(
			"🔍 [DEBUG] Final cumulative domain IDs set:",
			Array.from(cumulativeDataDomainIds),
		);

		// Second pass: Extract domains from the 'domain' array only
		let allDomains = [];

		// Handle different possible structures for domain array
		if (
			domainResults.body &&
			domainResults.body.domain &&
			Array.isArray(domainResults.body.domain)
		) {
			allDomains = domainResults.body.domain;
			console.log(
				"🔍 [DEBUG] Found domains in body.domain:",
				allDomains.length,
			);
		} else if (domainResults.domain && Array.isArray(domainResults.domain)) {
			allDomains = domainResults.domain;
			console.log(
				"🔍 [DEBUG] Found domains in domain array:",
				allDomains.length,
			);
		}

		console.log("🔍 [DEBUG] Total domains extracted:", allDomains.length);
		if (allDomains.length > 0) {
			console.log("🔍 [DEBUG] Sample domain structure:", allDomains[0]);
		}

		// Filter domains to only include those present in cumulativeData and remove duplicates
		const uniqueDomainsMap = new Map();
		let filteredCount = 0;
		let duplicateCount = 0;

		allDomains.forEach((domain) => {
			const domainIdStr = String(domain.id);

			// Only include domains that are in the cumulative data
			if (domain.id && cumulativeDataDomainIds.has(domainIdStr)) {
				if (!uniqueDomainsMap.has(domain.id)) {
					uniqueDomainsMap.set(domain.id, domain);
					filteredCount++;
					console.log("🔍 [DEBUG] ✅ Added domain:", {
						id: domain.id,
						name: domain.domainName || domain.name,
						score: domain.score,
					});
				} else {
					duplicateCount++;
					console.log("🔍 [DEBUG] ⚠️ Skipped duplicate domain:", domain.id);
				}
			} else {
				console.log(
					"🔍 [DEBUG] ❌ Domain ID not in cumulative data:",
					domainIdStr,
				);
			}
		});

		domains = Array.from(uniqueDomainsMap.values());
		console.log(`🔍 [DEBUG] Domain filtering summary:
      - Total domains found: ${allDomains.length}
      - Domains in cumulative data: ${cumulativeDataDomainIds.size}
      - Unique domains after filtering: ${domains.length}
      - Duplicates removed: ${duplicateCount}
      - Final domains to display: ${domains.length}`);
	} else if (domainResults && Array.isArray(domainResults)) {
		domains = domainResults;
		console.log(
			"🔍 [DEBUG] Using direct domain array (fallback):",
			domains.length,
		);
	}

	const filteredAudiences = audiences.filter((audience) => {
		// Extract data from nested audienceVector structure
		const audienceData = audience.audienceVector || audience;

		// Apply existing categorization logic
		const isOrgOA = isOAOrganization({ accountName: audienceData.accountName });
		const isProvOA = isOAProvider({
			audienceVector: {
				directAudienceProvider: audienceData.directAudienceProvider,
			},
		});

		// Check if audience matches selected filters
		const matchesProvider =
			!selectedProvider ||
			(selectedProvider === "OA" && isProvOA) ||
			(selectedProvider === "OAA" && !isProvOA);

		const matchesOrg =
			!selectedOrg ||
			(selectedOrg === "OA" && isOrgOA) ||
			(selectedOrg === "OAA" && !isOrgOA);

		return matchesProvider && matchesOrg;
	});

	console.log("🔍 [DEBUG] Filtered audiences count:", filteredAudiences.length);

	// Sort by composite score and take top 5
	const topAudiences = filteredAudiences
		.sort((a, b) => (b.compositeScore || 0) - (a.compositeScore || 0))
		.slice(0, 5);

	console.log(
		"🔍 [DEBUG] Top 5 audiences:",
		topAudiences.map((a) => ({
			id: (a.audienceVector || a).audienceId,
			name: (a.audienceVector || a).audienceName,
			score: a.compositeScore,
		})),
	);

	// Sort domains by score
	const sortedDomains = domains.sort((a, b) => (b.score || 0) - (a.score || 0));

	const hasAudiences = topAudiences.length > 0;
	const hasDomains = sortedDomains.length > 0;

	if (!hasAudiences && !hasDomains) {
		return (
			<div className="no-results">
				<span>No results found. Try adjusting your criteria.</span>
			</div>
		);
	}

	return (
		<div className="results-display">
			<div className="results-header">
				<h3>Package Results</h3>
				<div className="results-actions">
					<button
						onClick={() => {
							const allIds = [
								...topAudiences.map(
									(a) => `audience-${(a.audienceVector || a).audienceId}`,
								),
								...sortedDomains.map((d) => `domain-${d.id}`),
							];
							const hasAll = allIds.every((id) => selectedResults.includes(id));

							if (hasAll) {
								// Deselect all
								allIds.forEach((id) => {
									if (selectedResults.includes(id)) {
										const item = id.startsWith("audience-")
											? topAudiences.find(
													(a) =>
														`audience-${(a.audienceVector || a).audienceId}` ===
														id,
												)
											: sortedDomains.find((d) => `domain-${d.id}` === id);
										onResultToggle(id, item);
									}
								});
							} else {
								// Select all
								allIds.forEach((id) => {
									if (!selectedResults.includes(id)) {
										const item = id.startsWith("audience-")
											? topAudiences.find(
													(a) =>
														`audience-${(a.audienceVector || a).audienceId}` ===
														id,
												)
											: sortedDomains.find((d) => `domain-${d.id}` === id);
										onResultToggle(id, item);
									}
								});
							}
						}}
						className="select-all-btn"
					>
						Select All
					</button>
				</div>
			</div>

			{/* Audience Results Section */}
			{hasAudiences && (
				<div className="results-section">
					<div className="section-header">
						<h4>Top Audiences</h4>
						<span className="section-type">
							({topAudiences.length} results)
						</span>
					</div>
					<div className="cards-container">
						{topAudiences.map((audience) => (
							<AudienceCard
								key={(audience.audienceVector || audience).audienceId}
								audience={audience}
								isSelected={selectedResults.includes(
									`audience-${(audience.audienceVector || audience).audienceId}`,
								)}
								onToggle={onResultToggle}
								selectedProvider={selectedProvider}
								selectedOrg={selectedOrg}
							/>
						))}
					</div>
				</div>
			)}

			{/* Domain Results Section */}
			{hasDomains && (
				<div className="results-section">
					<div className="section-header">
						<h4>Domains</h4>
						<span className="section-type">
							({sortedDomains.length} results)
						</span>
					</div>
					<div className="cards-container">
						{sortedDomains.map((domain) => (
							<DomainCard
								key={domain.id}
								domain={domain}
								isSelected={selectedResults.includes(`domain-${domain.id}`)}
								onToggle={onResultToggle}
							/>
						))}
					</div>
				</div>
			)}

			{/* Empty Sections Info */}
			{!hasAudiences && (
				<div className="results-section empty">
					<div className="section-header">
						<h4>Audiences</h4>
						<span className="section-type">(No data)</span>
					</div>
					<div className="empty-message">No audience data received</div>
				</div>
			)}

			{!hasDomains && (
				<div className="results-section empty">
					<div className="section-header">
						<h4>Domains</h4>
						<span className="section-type">(No data)</span>
					</div>
					<div className="empty-message">No domain data received</div>
				</div>
			)}
		</div>
	);
};

// Step Indicator Component
const StepIndicator = ({ currentStep, totalSteps, stepLabels }) => {
	return (
		<div className="step-indicator">
			{Array.from({ length: totalSteps }, (_, index) => {
				const stepNumber = index + 1;
				const isCompleted = stepNumber < currentStep;
				const isCurrent = stepNumber === currentStep;

				return (
					<div key={stepNumber} className="step-item">
						<div
							className={`step-circle ${isCompleted ? "completed" : ""} ${isCurrent ? "current" : ""}`}
						>
							{isCompleted ? <Check size={14} /> : stepNumber}
						</div>
						<span className={`step-label ${isCurrent ? "current" : ""}`}>
							{stepLabels[index]}
						</span>
						{stepNumber < totalSteps && <div className="step-connector" />}
					</div>
				);
			})}
		</div>
	);
};

// Simple Multi-Select Component (without search)
const SimpleMultiSelect = ({
	title,
	description,
	options,
	selectedValues,
	onSelect,
	allowCustom = false,
	customPlaceholder = "Enter custom value",
}) => {
	const [isAddingCustom, setIsAddingCustom] = useState(false);
	const [customValue, setCustomValue] = useState("");

	const handleCustomSubmit = () => {
		if (customValue.trim()) {
			const newValue = customValue.trim();
			if (!selectedValues.includes(newValue)) {
				onSelect([...selectedValues, newValue]);
			}
			setCustomValue("");
			setIsAddingCustom(false);
		}
	};

	const toggleOption = (option) => {
		if (selectedValues.includes(option)) {
			onSelect(selectedValues.filter((v) => v !== option));
		} else {
			onSelect([...selectedValues, option]);
		}
	};

	const clearAll = () => {
		onSelect([]);
	};

	const selectAll = () => {
		onSelect([...options]);
	};

	return (
		<div className="simple-multi-select">
			<h3>{title}</h3>
			<p>{description}</p>

			{/* Quick Actions */}
			<div className="quick-actions">
				<button onClick={selectAll} className="quick-action-btn">
					Select All ({options.length})
				</button>
				<button onClick={clearAll} className="quick-action-btn clear">
					Clear ({selectedValues.length})
				</button>
			</div>

			{/* Options Grid */}
			<div className="options-grid">
				{options.map((option) => (
					<button
						key={option}
						onClick={() => toggleOption(option)}
						className={`option-button ${selectedValues.includes(option) ? "selected" : ""}`}
					>
						{option}
						{selectedValues.includes(option) && (
							<Check size={16} className="check-icon" />
						)}
					</button>
				))}

				{allowCustom && (
					<button
						onClick={() => setIsAddingCustom(true)}
						className="option-button add-custom"
					>
						<Plus size={16} />
						Add Custom
					</button>
				)}
			</div>

			{/* Selected Summary */}
			{selectedValues.length > 0 && (
				<div className="selected-summary">
					<span className="selected-count">
						{selectedValues.length} selected:
					</span>
					<div className="selected-tags">
						{selectedValues.map((value) => (
							<span key={value} className="selected-tag">
								{value}
								<button
									onClick={() => toggleOption(value)}
									className="remove-tag"
								>
									<X size={12} />
								</button>
							</span>
						))}
					</div>
				</div>
			)}

			{/* Custom Input */}
			{isAddingCustom && (
				<div className="custom-input">
					<input
						type="text"
						placeholder={customPlaceholder}
						value={customValue}
						onChange={(e) => setCustomValue(e.target.value)}
						onKeyPress={(e) => e.key === "Enter" && handleCustomSubmit()}
						autoFocus
					/>
					<button onClick={handleCustomSubmit} disabled={!customValue.trim()}>
						Add
					</button>
					<button
						onClick={() => {
							setIsAddingCustom(false);
							setCustomValue("");
						}}
					>
						Cancel
					</button>
				</div>
			)}
		</div>
	);
};

// Single Select Component for Audience Provider and Organization
const SingleSelectTile = ({ title, options, selectedValue, onSelect }) => {
	return (
		<div className="single-select-tile">
			<div className="tile-header">
				<h4>{title}</h4>
				{selectedValue && (
					<button
						onClick={() => onSelect("")}
						className="tile-action-btn clear"
					>
						Clear
					</button>
				)}
			</div>

			<div className="tile-options">
				{options.map((option) => (
					<button
						key={option}
						onClick={() => onSelect(option === selectedValue ? "" : option)}
						className={`tile-option ${selectedValue === option ? "selected" : ""}`}
					>
						{option}
						{selectedValue === option && (
							<Check size={14} className="check-icon" />
						)}
					</button>
				))}
			</div>

			{selectedValue && (
				<div className="tile-selected">
					<span className="selected-count">Selected: {selectedValue}</span>
				</div>
			)}
		</div>
	);
};

// Two-Tile Section Component
const TwoTileSection = ({
	title,
	description,
	tile1Title,
	tile1Options,
	tile1Selected,
	onTile1Select,
	tile2Title,
	tile2Options,
	tile2Selected,
	onTile2Select,
}) => {
	return (
		<div className="two-tile-section">
			<div className="section-header">
				<h3>{title}</h3>
				<p>{description}</p>
			</div>

			<div className="tiles-container">
				<div className="tile">
					<div className="tile-header">
						<h4>{tile1Title}</h4>
						<div className="tile-actions">
							<button
								onClick={() => onTile1Select(tile1Options)}
								className="tile-action-btn"
							>
								Select All
							</button>
							<button
								onClick={() => onTile1Select([])}
								className="tile-action-btn clear"
							>
								Clear
							</button>
						</div>
					</div>

					<div className="tile-options">
						{tile1Options.map((option) => (
							<button
								key={option}
								onClick={() => {
									if (tile1Selected.includes(option)) {
										onTile1Select(tile1Selected.filter((v) => v !== option));
									} else {
										onTile1Select([...tile1Selected, option]);
									}
								}}
								className={`tile-option ${tile1Selected.includes(option) ? "selected" : ""}`}
							>
								{option}
								{tile1Selected.includes(option) && (
									<Check size={14} className="check-icon" />
								)}
							</button>
						))}
					</div>

					{tile1Selected.length > 0 && (
						<div className="tile-selected">
							<span className="selected-count">
								{tile1Selected.length} selected
							</span>
						</div>
					)}
				</div>

				<div className="tile">
					<div className="tile-header">
						<h4>{tile2Title}</h4>
						<div className="tile-actions">
							<button
								onClick={() => onTile2Select(tile2Options)}
								className="tile-action-btn"
							>
								Select All
							</button>
							<button
								onClick={() => onTile2Select([])}
								className="tile-action-btn clear"
							>
								Clear
							</button>
						</div>
					</div>

					<div className="tile-options">
						{tile2Options.map((option) => (
							<button
								key={option}
								onClick={() => {
									if (tile2Selected.includes(option)) {
										onTile2Select(tile2Selected.filter((v) => v !== option));
									} else {
										onTile2Select([...tile2Selected, option]);
									}
								}}
								className={`tile-option ${tile2Selected.includes(option) ? "selected" : ""}`}
							>
								{option}
								{tile2Selected.includes(option) && (
									<Check size={14} className="check-icon" />
								)}
							</button>
						))}
					</div>

					{tile2Selected.length > 0 && (
						<div className="tile-selected">
							<span className="selected-count">
								{tile2Selected.length} selected
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

// Audience Provider and Organization Section Component
const AudienceOrgSection = ({
	audienceProvider,
	onAudienceProviderSelect,
	organization,
	onOrganizationSelect,
}) => {
	const audienceProviderOptions = ["OA", "OAA"];
	const organizationOptions = ["OA", "OAA"];

	return (
		<div className="two-tile-section">
			<div className="section-header">
				<h3>Audience Provider & Organization</h3>
				<p>Select your audience provider and organization (one option each)</p>
			</div>

			<div className="tiles-container">
				<SingleSelectTile
					title="Audience Provider"
					options={audienceProviderOptions}
					selectedValue={audienceProvider}
					onSelect={onAudienceProviderSelect}
				/>
				<SingleSelectTile
					title="Organization"
					options={organizationOptions}
					selectedValue={organization}
					onSelect={onOrganizationSelect}
				/>
			</div>
		</div>
	);
};

const CreatePackageModal = ({ isOpen, onClose }) => {
	// Wizard state
	const [currentStep, setCurrentStep] = useState(1);
	const [isLoading, setIsLoading] = useState(false);

	// Global search state
	const [globalSearchQuery, setGlobalSearchQuery] = useState("");
	const [isGlobalSearching, setIsGlobalSearching] = useState(false);

	// Form data - arrays for multiple selections, strings for single selections
	const [formData, setFormData] = useState({
		// Step 1: Location
		countries: [],

		// Step 2: Audience Provider & Organization
		audienceProvider: "",
		organization: "",
		publisherIds: [],

		// Step 3: Technology and Devices
		deviceTypes: [],
		languages: [],

		// Step 4: Category
		categories: [],

		// Step 5: Format Type
		distributionChannels: [],
		adPlacements: [],
	});

	// Results state
	const [audienceResults, setAudienceResults] = useState([]);
	const [domainResults, setDomainResults] = useState([]);
	const [selectedResults, setSelectedResults] = useState([]);
	const [selectedResultsData, setSelectedResultsData] = useState([]);

	const stepLabels = [
		"Location",
		"Audience & Organization",
		"Technology & Devices",
		"Category",
		"Format Type",
		"Results",
	];
	const totalSteps = stepLabels.length;

	// Form options
	const formOptions = {
		countries: [
			"United States",
			"Canada",
			"United Kingdom",
			"Germany",
			"France",
			"Australia",
			"Japan",
			"India",
			"Brazil",
			"Spain",
			"Italy",
			"Netherlands",
			"Mexico",
			"South Korea",
			"Sweden",
			"Norway",
			"Denmark",
			"Finland",
			"Switzerland",
			"Austria",
			"Belgium",
			"Ireland",
		],
		publisherIds: [
			"pub-1234567890123456",
			"pub-2345678901234567",
			"pub-3456789012345678",
			"pub-4567890123456789",
			"pub-5678901234567890",
			"pub-6789012345678901",
			"pub-7890123456789012",
			"pub-8901234567890123",
			"pub-9012345678901234",
		],
		deviceTypes: [
			"Mobile",
			"Desktop",
			"Tablet",
			"Connected TV",
			"Gaming Console",
			"Smart TV",
			"Smartphone",
			"Laptop",
			"Smart Watch",
			"Voice Assistant",
			"Streaming Device",
		],
		languages: [
			"English",
			"Spanish",
			"French",
			"German",
			"Italian",
			"Portuguese",
			"Russian",
			"Chinese (Simplified)",
			"Chinese (Traditional)",
			"Japanese",
			"Korean",
			"Arabic",
			"Hindi",
			"Dutch",
			"Swedish",
			"Norwegian",
			"Danish",
			"Finnish",
		],
		categories: [
			"Entertainment",
			"News",
			"Sports",
			"Technology",
			"Finance",
			"Travel",
			"Health",
			"Education",
			"Gaming",
			"Lifestyle",
			"Business",
			"Science",
			"Automotive",
			"Real Estate",
			"Food & Dining",
			"Fashion",
			"Music",
			"Movies",
			"Books",
			"Art & Culture",
		],
		distributionChannels: [
			"Web",
			"Mobile App",
			"In-App",
			"Video",
			"Display",
			"Native",
			"Audio",
			"Social Media",
			"Email",
			"Search",
			"Programmatic",
			"Direct",
			"Connected TV",
			"Podcast",
			"Streaming",
		],
		adPlacements: [
			"Banner",
			"Interstitial",
			"Native",
			"Video Pre-roll",
			"Video Mid-roll",
			"Video Post-roll",
			"Rewarded Video",
			"Overlay",
			"Sidebar",
			"Footer",
			"Header",
			"In-feed",
			"Pop-up",
			"Pop-under",
		],
	};

	// Prevent body scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isOpen]);

	// Check if service is available when modal opens
	useEffect(() => {
		if (isOpen) {
			// Check if service is available
			const checkService = () => {
				if (typeof window !== "undefined" && window.packageModalApiService) {
					console.log("✅ PackageModalApiService found on window");
					return true;
				}
				if (packageModalApiService) {
					console.log("✅ PackageModalApiService imported directly");
					return true;
				}
				console.warn("⚠️ PackageModalApiService not available");
				return false;
			};

			checkService();

			setCurrentStep(1);
			setGlobalSearchQuery("");
			setFormData({
				countries: [],
				audienceProvider: "",
				organization: "",
				publisherIds: [],
				deviceTypes: [],
				languages: [],
				categories: [],
				distributionChannels: [],
				adPlacements: [],
			});
			setAudienceResults([]);
			setDomainResults([]);
			setSelectedResults([]);
			setSelectedResultsData([]);
		}
	}, [isOpen]);

	const updateFormData = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	// Combined search function that integrates global search query with targeting criteria
	const performAPISearch = async (searchQuery = "", targetingCriteria = {}) => {
		console.log("🔍 Starting API search with:", {
			searchQuery,
			targetingCriteria,
		});

		// Check if API service is available
		const apiService = window.packageModalApiService;
		if (!apiService) {
			console.error("❌ PackageModalApiService not found on window object");
			throw new Error("API service not available");
		}

		// Check authentication
		if (!apiService.isAuthenticated()) {
			console.error("❌ User not authenticated");
			throw new Error("Authentication required");
		}

		// Build search parameters combining global search and targeting
		const searchParams = {
			searchQuery: searchQuery.trim(),
			countries: targetingCriteria.countries || [],
			deviceTypes: targetingCriteria.deviceTypes || [],
			categories: targetingCriteria.categories || [],
			distributionChannels: targetingCriteria.distributionChannels || [],
			audienceProvider: targetingCriteria.audienceProvider || "",
			organization: targetingCriteria.organization || "",
			publisherIds: targetingCriteria.publisherIds || [],
			languages: targetingCriteria.languages || [],
			adPlacements: targetingCriteria.adPlacements || [],
		};

		console.log("📋 Final search parameters:", searchParams);

		// Call the combined search API
		const response = await apiService.searchPackageModalResults(searchParams);

		if (!response.success) {
			console.error("❌ API search failed:", response.error);
			throw new Error(response.error || "Search failed");
		}

		console.log("✅ API search successful:", response.data);
		return response.data;
	};

	const processAPIResults = (apiData) => {
		console.log("Processing API results:", apiData);

		// Extract raw audience data - get from body property
		let audienceData = apiData.audiences || {};
		if (audienceData.body) {
			audienceData = audienceData.body;
			console.log("🔍 [DEBUG] Audience data from body:", audienceData);
		}

		// Extract raw domain data - get from body property
		let domainData = apiData.domains || {};
		if (domainData.body) {
			domainData = domainData.body;
		}

		console.log("🔍 [DEBUG] Final processed audience data:", audienceData);
		console.log("🔍 [DEBUG] Audience data type:", typeof audienceData);
		console.log(
			"🔍 [DEBUG] Is audience data array?",
			Array.isArray(audienceData),
		);

		return {
			audiences: audienceData,
			domains: domainData,
			audienceResponse: apiData.audienceResponse,
			domainResponse: apiData.domainResponse,
		};
	};

	// Global search function that uses real API and preserves form data
	const handleGlobalSearch = async () => {
		if (!globalSearchQuery.trim()) return;

		setIsGlobalSearching(true);

		try {
			console.log("🌐 Starting global search:", globalSearchQuery);
			console.log("🎯 Including form filters:", formData);

			// Use real API with BOTH global search query AND current form data
			const apiData = await performAPISearch(globalSearchQuery, formData);

			// Process results
			const { audiences, domains } = processAPIResults(apiData);

			setAudienceResults(audiences);
			setDomainResults(domains);
			setCurrentStep(totalSteps); // Jump to results

			console.log("✅ Global search completed successfully");
		} catch (error) {
			console.error("❌ Global search failed:", error);

			// Set empty results on error
			setAudienceResults([]);
			setDomainResults([]);
			setCurrentStep(totalSteps);
		} finally {
			setIsGlobalSearching(false);
		}
	};

	// Fetch results function that uses real API and includes global search query
	const fetchResults = async () => {
		setIsLoading(true);

		try {
			console.log("📊 Fetching results with targeting criteria:", formData);
			console.log("🔍 Including global search query:", globalSearchQuery);

			// Use real API with BOTH targeting criteria AND global search query
			const apiData = await performAPISearch(globalSearchQuery, formData);

			// Process results
			const { audiences, domains } = processAPIResults(apiData);

			setAudienceResults(audiences);
			setDomainResults(domains);

			console.log("✅ Targeted search completed successfully");
		} catch (error) {
			console.error("❌ Targeted search failed:", error);

			// Set empty results on error
			setAudienceResults([]);
			setDomainResults([]);
		} finally {
			setIsLoading(false);
		}
	};

	// Updated handleNext function to preserve search query when moving to results
	const handleNext = async () => {
		if (currentStep < totalSteps - 1) {
			setCurrentStep((prev) => prev + 1);
		} else if (currentStep === totalSteps - 1) {
			// Fetch results when moving to results step
			// This will now include both the global search query AND form data
			await fetchResults();
			setCurrentStep(totalSteps);
		}
	};

	const handleBack = () => {
		if (currentStep > 1) {
			setCurrentStep((prev) => prev - 1);
		}
	};

	// Function to clear everything and start fresh
	const handleClearAll = () => {
		setGlobalSearchQuery("");
		setFormData({
			countries: [],
			audienceProvider: "",
			organization: "",
			publisherIds: [],
			deviceTypes: [],
			languages: [],
			categories: [],
			distributionChannels: [],
			adPlacements: [],
		});
		setAudienceResults([]);
		setDomainResults([]);
		setSelectedResults([]);
		setSelectedResultsData([]);
		setCurrentStep(1);
	};

	// Enhanced search status display for the results page
	const getSearchSummary = () => {
		const hasGlobalSearch = globalSearchQuery.trim();
		const hasFilters = Object.values(formData).some((value) =>
			Array.isArray(value) ? value.length > 0 : value !== "",
		);

		if (hasGlobalSearch && hasFilters) {
			return `Results for "${globalSearchQuery}" with applied filters`;
		} else if (hasGlobalSearch) {
			return `Global search results for "${globalSearchQuery}"`;
		} else if (hasFilters) {
			return `Targeted results based on your criteria`;
		} else {
			return `No search criteria specified`;
		}
	};

	const handleResultToggle = (resultId, resultData) => {
		setSelectedResults((prev) => {
			const newSelected = prev.includes(resultId)
				? prev.filter((id) => id !== resultId)
				: [...prev, resultId];

			// Update selected results data
			setSelectedResultsData((prevData) => {
				if (prev.includes(resultId)) {
					return prevData.filter((item) => item.id !== resultId);
				} else {
					return [...prevData, { id: resultId, ...resultData }];
				}
			});

			return newSelected;
		});
	};

	const handleSelectAll = (selectAll) => {
		if (selectAll) {
			const allIds = [
				...audienceResults.map((r) => `audience-${r.id}`),
				...domainResults.map((r) => `domain-${r.id}`),
			];
			const allData = [
				...audienceResults.map((r) => ({
					id: `audience-${r.id}`,
					type: "audience",
					...r,
				})),
				...domainResults.map((r) => ({
					id: `domain-${r.id}`,
					type: "domain",
					...r,
				})),
			];
			setSelectedResults(allIds);
			setSelectedResultsData(allData);
		} else {
			setSelectedResults([]);
			setSelectedResultsData([]);
		}
	};

	const handleExport = () => {
		if (selectedResultsData.length === 0) {
			alert("Please select at least one result to export.");
			return;
		}

		const exportData = {
			searchQuery: globalSearchQuery || null,
			packageCriteria: formData,
			selectedResults: selectedResultsData,
			exportDate: new Date().toISOString(),
			totalSelected: selectedResultsData.length,
		};

		// Create and download JSON file
		const dataStr = JSON.stringify(exportData, null, 2);
		const dataUri =
			"data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

		const exportFileDefaultName = `package-results-${new Date().toISOString().split("T")[0]}.json`;

		const linkElement = document.createElement("a");
		linkElement.setAttribute("href", dataUri);
		linkElement.setAttribute("download", exportFileDefaultName);
		linkElement.click();
	};

	const canProceed = () => {
		switch (currentStep) {
			case 1:
				return formData.countries.length > 0;
			case 2:
				return (
					formData.audienceProvider ||
					formData.organization ||
					formData.publisherIds.length > 0
				);
			case 3:
				return formData.deviceTypes.length > 0 || formData.languages.length > 0;
			case 4:
				return formData.categories.length > 0;
			case 5:
				return (
					formData.distributionChannels.length > 0 ||
					formData.adPlacements.length > 0
				);
			default:
				return false;
		}
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return (
					<SimpleMultiSelect
						title="Select Target Locations"
						description="Choose one or more countries for your advertising package"
						options={formOptions.countries}
						selectedValues={formData.countries}
						onSelect={(values) => updateFormData("countries", values)}
						allowCustom={true}
						customPlaceholder="Enter custom country"
					/>
				);
			case 2:
				return (
					<div>
						<AudienceOrgSection
							audienceProvider={formData.audienceProvider}
							onAudienceProviderSelect={(value) =>
								updateFormData("audienceProvider", value)
							}
							organization={formData.organization}
							onOrganizationSelect={(value) =>
								updateFormData("organization", value)
							}
						/>

						<div style={{ marginTop: "32px" }}>
							<SimpleMultiSelect
								title="Publisher IDs (Optional)"
								description="Choose publisher IDs if needed for your advertising package"
								options={formOptions.publisherIds}
								selectedValues={formData.publisherIds}
								onSelect={(values) => updateFormData("publisherIds", values)}
								allowCustom={true}
								customPlaceholder="Enter custom publisher ID"
							/>
						</div>
					</div>
				);
			case 3:
				return (
					<TwoTileSection
						title="Technology and Devices"
						description="Choose device types and languages for your advertising package"
						tile1Title="Device Types"
						tile1Options={formOptions.deviceTypes}
						tile1Selected={formData.deviceTypes}
						onTile1Select={(values) => updateFormData("deviceTypes", values)}
						tile2Title="Languages"
						tile2Options={formOptions.languages}
						tile2Selected={formData.languages}
						onTile2Select={(values) => updateFormData("languages", values)}
					/>
				);
			case 4:
				return (
					<SimpleMultiSelect
						title="Select Categories"
						description="Choose one or more content categories for your advertising package"
						options={formOptions.categories}
						selectedValues={formData.categories}
						onSelect={(values) => updateFormData("categories", values)}
						allowCustom={true}
						customPlaceholder="Enter custom category"
					/>
				);
			case 5:
				return (
					<TwoTileSection
						title="Format Type"
						description="Choose distribution channels and ad placements for your advertising package"
						tile1Title="Distribution Channel"
						tile1Options={formOptions.distributionChannels}
						tile1Selected={formData.distributionChannels}
						onTile1Select={(values) =>
							updateFormData("distributionChannels", values)
						}
						tile2Title="Ad Placement"
						tile2Options={formOptions.adPlacements}
						tile2Selected={formData.adPlacements}
						onTile2Select={(values) => updateFormData("adPlacements", values)}
					/>
				);
			case 6:
				return (
					<div>
						{/* Search Summary */}
						<div className="search-summary">
							<h3>{getSearchSummary()}</h3>
							{(globalSearchQuery.trim() ||
								Object.values(formData).some((value) =>
									Array.isArray(value) ? value.length > 0 : value !== "",
								)) && (
								<button
									onClick={handleClearAll}
									className="clear-all-button"
									title="Clear all filters and search terms"
								>
									Clear All & Start Over
								</button>
							)}
						</div>

						<ResultsDisplay
							audienceResults={audienceResults}
							domainResults={domainResults}
							isLoading={isLoading}
							selectedProvider={formData.audienceProvider}
							selectedOrg={formData.organization}
							selectedResults={selectedResults}
							onResultToggle={handleResultToggle}
						/>
					</div>
				);
			default:
				return null;
		}
	};

	if (!isOpen) return null;

	return (
		<div className="modal-overlay">
			<div className="create-package-modal">
				{/* Header */}
				<div className="modal-header">
					<div className="header-content">
						<h2>Create New Package</h2>
						<p>
							Search globally or follow guided steps to build your targeting
							package
						</p>
					</div>
					<button onClick={onClose} className="modal-close-btn">
						<X size={20} />
					</button>
				</div>

				{/* Global Search Bar */}
				<div className="global-search-section">
					<div className="global-search-container">
						<div className="global-search-wrapper">
							<Search size={16} className="global-search-icon" />
							<input
								type="text"
								placeholder="Search for audiences, domains, or keywords globally..."
								value={globalSearchQuery}
								onChange={(e) => setGlobalSearchQuery(e.target.value)}
								onKeyPress={(e) => e.key === "Enter" && handleGlobalSearch()}
								className="global-search-input"
							/>
						</div>
						<button
							onClick={handleGlobalSearch}
							disabled={isGlobalSearching || !globalSearchQuery.trim()}
							className="global-search-button"
						>
							{isGlobalSearching ? "Searching..." : "Search"}
						</button>
					</div>
				</div>

				{/* Step Indicator */}
				{currentStep < totalSteps && (
					<div className="step-indicator-section">
						<StepIndicator
							currentStep={currentStep}
							totalSteps={totalSteps - 1}
							stepLabels={stepLabels.slice(0, -1)}
						/>
					</div>
				)}

				{/* Main Content */}
				<div className="modal-content">
					<div className="step-content">{renderStepContent()}</div>
				</div>

				{/* Footer */}
				<div className="modal-footer">
					<div className="footer-info">
						{currentStep === totalSteps && (
							<span className="selection-count">
								{selectedResults.length} items selected
							</span>
						)}
					</div>
					<div className="footer-actions">
						{currentStep > 1 && currentStep < totalSteps && (
							<button onClick={handleBack} className="back-button">
								<ArrowLeft size={16} />
								Back
							</button>
						)}

						{currentStep < totalSteps && (
							<button onClick={handleNext} className="next-button">
								{currentStep === totalSteps - 1 ? "Get Results" : "Next"}
								<ArrowRight size={16} />
							</button>
						)}

						{currentStep === totalSteps && (
							<button
								onClick={handleExport}
								disabled={selectedResults.length === 0}
								className="export-button"
							>
								<Download size={16} />
								Export Selected ({selectedResults.length})
							</button>
						)}
					</div>
				</div>
			</div>

			<style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          overflow: hidden;
        }

        .create-package-modal {
          background: white;
          border-radius: 16px;
          width: 95vw;
          height: 90vh;
          max-width: 1400px;
          max-height: 900px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        /* Header */
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 20px 32px;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          flex-shrink: 0;
        }

        .header-content h2 {
          margin: 0 0 4px 0;
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
        }

        .header-content p {
          margin: 0;
          font-size: 15px;
          color: #64748b;
        }

        .modal-close-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #64748b;
          padding: 8px;
          border-radius: 8px;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close-btn:hover {
          background: #f1f5f9;
          color: #374151;
        }

        /* Global Search Section */
        .global-search-section {
          padding: 16px 32px;
          background: #fafbfc;
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
        }

        .global-search-container {
          display: flex;
          gap: 12px;
          align-items: center;
          max-width: 600px;
          margin: 0 auto;
        }

        .global-search-wrapper {
          position: relative;
          flex: 1;
        }

        .global-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          z-index: 1;
        }

        .global-search-input {
          width: 100%;
          padding: 12px 12px 12px 40px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          transition: all 0.2s;
          outline: none;
        }

        .global-search-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .global-search-button {
          padding: 12px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
          min-width: 100px;
        }

        .global-search-button:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .global-search-button:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          transform: none;
          color: white;
        }

        /* Search Summary Section */
        .search-summary {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }

        .search-summary h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
        }

        .clear-all-button {
          padding: 8px 16px;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .clear-all-button:hover {
          background: #b91c1c;
          transform: translateY(-1px);
        }

        /* Step Indicator */
        .step-indicator-section {
          padding: 16px 32px;
          background: #f8fafc;
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
        }

        .step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .step-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          flex: 1;
          max-width: 140px;
        }

        .step-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #e5e7eb;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 6px;
          transition: all 0.2s;
        }

        .step-circle.current {
          background: #3b82f6;
          color: white;
        }

        .step-circle.completed {
          background: #10b981;
          color: white;
        }

        .step-label {
          font-size: 12px;
          color: #6b7280;
          text-align: center;
          font-weight: 500;
        }

        .step-label.current {
          color: #3b82f6;
          font-weight: 600;
        }

        .step-connector {
          position: absolute;
          top: 16px;
          left: 50%;
          width: 100%;
          height: 2px;
          background: #e5e7eb;
          z-index: -1;
        }

        /* Main Content */
        .modal-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px 32px;
        }

        .step-content {
          max-width: 1000px;
          margin: 0 auto;
        }

        /* Simple Multi-Select Component */
        .simple-multi-select h3 {
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
          text-align: center;
        }

        .simple-multi-select p {
          margin: 0 0 24px 0;
          font-size: 16px;
          color: #64748b;
          text-align: center;
        }

        .quick-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 20px;
        }

        .quick-action-btn {
          padding: 8px 16px;
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .quick-action-btn:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .quick-action-btn.clear {
          color: #dc2626;
          border-color: #fca5a5;
        }

        .quick-action-btn.clear:hover {
          background: #fef2f2;
          border-color: #f87171;
        }

        .options-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }

        .option-button {
          padding: 16px 20px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          background: white;
          color: #374151;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          position: relative;
          min-height: 60px;
        }

        .option-button:hover {
          border-color: #3b82f6;
          background: #f8fafc;
        }

        .option-button.selected {
          border-color: #3b82f6;
          background: #eff6ff;
          color: #1d4ed8;
        }

        .option-button.add-custom {
          border-style: dashed;
          border-color: #9ca3af;
          color: #6b7280;
        }

        .option-button.add-custom:hover {
          border-color: #3b82f6;
          color: #3b82f6;
        }

        .check-icon {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #10b981;
          color: white;
          border-radius: 50%;
          padding: 2px;
        }

        /* Single Select Tile Component */
        .single-select-tile {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
        }

        /* Two-Tile Section */
        .two-tile-section {
          width: 100%;
        }

        .section-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .section-header h3 {
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
        }

        .section-header p {
          margin: 0;
          font-size: 16px;
          color: #64748b;
        }

        .tiles-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .tile {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
        }

        .tile-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 8px;
        }

        .tile-header h4 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
        }

        .tile-actions {
          display: flex;
          gap: 8px;
        }

        .tile-action-btn {
          padding: 6px 12px;
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tile-action-btn:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .tile-action-btn.clear {
          color: #dc2626;
          border-color: #fca5a5;
        }

        .tile-action-btn.clear:hover {
          background: #fef2f2;
          border-color: #f87171;
        }

        .tile-options {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 8px;
          margin-bottom: 12px;
        }

        .tile-option {
          padding: 12px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          color: #374151;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          min-height: 44px;
        }

        .tile-option:hover {
          border-color: #3b82f6;
          background: #f8fafc;
        }

        .tile-option.selected {
          border-color: #3b82f6;
          background: #eff6ff;
          color: #1d4ed8;
        }

        .tile-option .check-icon {
          position: absolute;
          top: 4px;
          right: 4px;
          background: #10b981;
          color: white;
          border-radius: 50%;
          padding: 1px;
        }

        .tile-selected {
          text-align: center;
          padding: 8px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 12px;
          color: #374151;
          font-weight: 500;
        }

        /* Selected Summary */
        .selected-summary {
          margin-top: 20px;
          padding: 20px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
        }

        .selected-count {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
          display: block;
        }

        .selected-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .selected-tag {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
        }

        .remove-tag {
          background: none;
          border: none;
          color: #1d4ed8;
          cursor: pointer;
          padding: 0;
          display: flex;
          align-items: center;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .remove-tag:hover {
          background: #dbeafe;
        }

        .custom-input {
          display: flex;
          gap: 12px;
          align-items: center;
          justify-content: center;
          margin-top: 16px;
        }

        .custom-input input {
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
          min-width: 200px;
        }

        .custom-input input:focus {
          border-color: #3b82f6;
        }

        .custom-input button {
          padding: 12px 18px;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .custom-input button:first-of-type {
          background: #3b82f6;
          color: white;
        }

        .custom-input button:first-of-type:hover {
          background: #2563eb;
        }

        .custom-input button:first-of-type:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .custom-input button:last-of-type {
          background: #f3f4f6;
          color: #374151;
        }

        .custom-input button:last-of-type:hover {
          background: #e5e7eb;
        }

        /* Results Display - Card-based View */
        .results-display {
          width: 100%;
        }

        .results-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .results-header h3 {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
        }

        .results-actions {
          display: flex;
          gap: 8px;
        }

        .select-all-btn {
          padding: 8px 16px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .select-all-btn:hover {
          background: #2563eb;
        }

        .results-section {
          margin-bottom: 32px;
        }

        .results-section.empty {
          border: 1px solid #f3f4f6;
          border-radius: 8px;
          background: #fafbfc;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 2px solid #e5e7eb;
        }

        .results-section.empty .section-header {
          background: #f9fafb;
          margin-bottom: 0;
          padding: 12px 16px;
          border-bottom: 1px solid #e5e7eb;
        }

        .section-header h4 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
        }

        .section-type {
          font-size: 12px;
          color: #64748b;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 12px;
          font-weight: 500;
        }

        .cards-container {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 16px;
        }

        /* Result Card Styles */
        .result-card {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .result-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .result-card.selected {
          border-color: #3b82f6;
          background: #eff6ff;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
          gap: 12px;
        }

        .card-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          line-height: 1.4;
          flex: 1;
        }

        .card-score {
          display: flex;
          flex-direction: column;
          align-items: center;
          background: #f8fafc;
          padding: 8px 12px;
          border-radius: 8px;
          min-width: 60px;
        }

        .score-label {
          font-size: 10px;
          color: #64748b;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .score-value {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
          margin-top: 2px;
        }

        .card-metrics {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 16px;
        }

        .metric {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .metric-label {
          font-size: 11px;
          color: #64748b;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .metric-value {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
        }

        .card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 12px;
          border-top: 1px solid #f1f5f9;
        }

        .audience-type,
        .domain-category,
        .account-name {
          font-size: 12px;
          color: #64748b;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 6px;
          font-weight: 500;
        }

        .selected-icon {
          color: #10b981;
          background: white;
          border-radius: 50%;
          padding: 2px;
        }

        /* Audience Card Specific Styles */
        .audience-card {
          border-left: 4px solid #3b82f6;
        }

        .audience-card.selected {
          border-left-color: #1d4ed8;
        }

        /* Domain Card Specific Styles */
        .domain-card {
          border-left: 4px solid #10b981;
        }

        .domain-card.selected {
          border-left-color: #047857;
        }

        .empty-message {
          padding: 32px;
          text-align: center;
          color: #9ca3af;
          font-style: italic;
        }

        .loading-state,
        .no-results {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
          color: #64748b;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #f1f5f9;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Footer */
        .modal-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 32px;
          border-top: 1px solid #e5e7eb;
          background: #fafbfc;
          flex-shrink: 0;
        }

        .footer-info {
          color: #64748b;
          font-size: 14px;
        }

        .selection-count {
          font-weight: 500;
        }

        .footer-actions {
          display: flex;
          gap: 12px;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: white;
          color: #64748b;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .back-button:hover {
          background: #f8fafc;
          border-color: #9ca3af;
        }

        .next-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .next-button:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .next-button:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          transform: none;
        }

        .export-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .export-button:hover:not(:disabled) {
          background: #059669;
          transform: translateY(-1px);
        }

        .export-button:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          transform: none;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .create-package-modal {
            width: 98vw;
            height: 95vh;
          }

          .tiles-container {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .options-grid {
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          }

          .tile-options {
            grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .global-search-container {
            flex-direction: column;
            gap: 8px;
          }

          .global-search-button {
            align-self: stretch;
          }

          .options-grid {
            grid-template-columns: 1fr;
          }

          .tile-options {
            grid-template-columns: 1fr;
          }

          .custom-input {
            flex-direction: column;
          }

          .custom-input input {
            min-width: auto;
            width: 100%;
          }

          .step-indicator {
            flex-wrap: wrap;
            gap: 8px;
          }

          .step-item {
            flex: none;
            min-width: 80px;
          }

          .quick-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .results-actions {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }

          .tile-header {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
		</div>
	);
};

export default CreatePackageModal;
